import { StartWizardModule } from '@/start-wizard/start-wizard.module';
import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideTranslateService, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { JwtModule } from '@auth0/angular-jwt';
import { CommonModule } from '@angular/common';
import { HeaderInterceptor } from '@/core/services/interceptors/header.interceptor';
import { MessageService, ConfirmationService } from 'primeng/api';
import { provideGoogleTagManager } from 'angular-google-tag-manager';
import { LocalStorageService } from '@/core/services/utils/local-storage.service';
import { providePrimeNG } from 'primeng/config';
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { WebConsoleModule } from '@/home/web-console.module';
import { AppCommonModule } from '@/common/app.common.module';
import { PagesModule } from '@/pages/pages.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Message } from 'primeng/message';
import { ErrorInterceptor } from '@/core/services/interceptors/error.interceptor';
import { ButtonDirective } from 'primeng/button';

export function tokenGetter(): string | null {
    return LocalStorageService.getToken();
}

export function getCspNonce(): string | undefined {
    const cspNonce = document.querySelector<HTMLMetaElement>('meta[name="csp-nonce"]')?.content;
    return cspNonce;
}

const ThemeStylePreset = definePreset(Aura, {
    semantic: {
        primary: {
            50: '{blue.50}',
            100: '{blue.100}',
            200: '{blue.200}',
            300: '{blue.300}',
            400: '{blue.400}',
            500: '{blue.500}',
            600: '{blue.600}',
            700: '{blue.700}',
            800: '{blue.800}',
            900: '{blue.900}',
            950: '{blue.950}',
        },
    },
});

@NgModule({
    declarations: [AppComponent],
    imports: [
        BrowserModule,
        BrowserAnimationsModule, // module approach for animations... Needed by theme.
        CommonModule,
        AppRoutingModule,
        ProgressSpinnerModule,
        ToastModule,
        // HttpClientModule,
        JwtModule.forRoot({
            config: {
                tokenGetter: tokenGetter,
            },
        }),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: () =>
                    provideTranslateHttpLoader({
                        prefix: './assets/i18n/',
                        suffix: '.json?v=4.3',
                    }),
            },
        }),
        // app modules:
        StartWizardModule,
        WebConsoleModule,
        AppCommonModule,
        PagesModule,
        ConfirmDialog,
        Message,
        ButtonDirective,
    ],
    providers: [
        MessageService,
        ConfirmationService,
        provideHttpClient(withInterceptorsFromDi()),
        { provide: HTTP_INTERCEPTORS, useClass: HeaderInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
        provideTranslateService({
            loader: provideTranslateHttpLoader({
                prefix: './assets/i18n/',
                suffix: '.json?v=4.3',
            }),
        }),
        // Theme:
        provideAnimationsAsync(),
        providePrimeNG({ theme: { preset: ThemeStylePreset, options: { darkModeSelector: '.app-dark' } } }),
        // Google Tag Manager - please note that the provider must be used in order to use csp nonce correctly!
        provideGoogleTagManager({
            id: 'GTM-W8399CQ',
            gtm_csp_none: getCspNonce(),
        }),
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}
